import React, { useState, useRef } from 'react';
import {
  Box,
  Flex,
  VStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { InputArea, InputAreaRef } from './InputArea';
import { ResponseViewer, ResponseViewerRef } from './ResponseViewer';
import { AssistantChatClientRefactored, AssistantChatClientRef } from './AssistantChatClientRefactored';
import { Config } from '../../types/config';

interface HomeProps {
  selectedConfig: Config | null;
  onGitLabFileUploadRequest?: (callback: (variable: string, gitlabUrl: string, filePath: string) => Promise<void>) => void;
  onToggleGitlabBrowser?: () => void;
}

export const Home: React.FC<HomeProps> = ({ selectedConfig, onGitLabFileUploadRequest, onToggleGitlabBrowser }) => {
  const [gitlabSourcePath, setGitlabSourcePath] = useState<string | null>(null);
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [isInputPanelCollapsed, setIsInputPanelCollapsed] = useState(true);
  const [currentInputs, setCurrentInputs] = useState<any>({});
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const inputAreaRef = useRef<InputAreaRef>(null);
  const responseViewerRef = useRef<ResponseViewerRef>(null);
  const assistantChatRef = useRef<AssistantChatClientRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Debug input changes
  React.useEffect(() => {
    console.log('Home currentInputs updated:', currentInputs);
  }, [currentInputs]);

  // Monitor container height for auto-docking
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const height = entry.contentRect.height;
        setContainerHeight(height);
        
        // Auto-dock when height is limited (less than 600px) or response viewer expands
        const shouldAutoDock = height < 600;
        const isChatApplication = selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat';
        
        if (shouldAutoDock && !isInputPanelCollapsed) {
          setIsInputPanelCollapsed(true);
        }
        
        // Auto-expand when sufficient height is available (more than 800px) for non-chat apps
        const shouldAutoExpand = height > 800 && !isChatApplication;
        if (shouldAutoExpand && isInputPanelCollapsed) {
          setIsInputPanelCollapsed(false);
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [selectedConfig, isInputPanelCollapsed]);
  
  // Move all hooks to the top
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleResponse = (content: string, streaming = false) => {
    if (streaming) {
      // For streaming, append the content chunk
      responseViewerRef.current?.appendContent(content);
    } else {
      // For non-streaming, set the entire content
      responseViewerRef.current?.setContent(content);
    }
  };

  const handleError = (errorMessage: string) => {
    responseViewerRef.current?.setError(errorMessage);
  };

  const handleStartRequest = () => {
    // Check if this is a chat application
    const isChatApplication = selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat';
    
    if (isChatApplication) {
      // For chat applications, show chat interface (InputArea will handle its own collapsing)
      setShowChatInterface(true);
      responseViewerRef.current?.reset();
    } else {
      // Normal behavior for document applications - ensure complete reset
      responseViewerRef.current?.reset(); // Complete reset first
      responseViewerRef.current?.startStreaming(); // Then start streaming
    }
  };

  const handleComplete = () => {
    responseViewerRef.current?.stopStreaming();
  };

  const handleStop = () => {
    // Stop processing in InputArea (which handles abort controller)
    inputAreaRef.current?.stopProcessing();
    responseViewerRef.current?.stopStreaming();
  };

  // Store previous config ID to detect actual changes
  const [previousConfigId, setPreviousConfigId] = React.useState<string | null>(null);
  
  // Handle config changes - NO MORE AUTOMATIC RESET for memory persistence
  React.useEffect(() => {
    // Only stop ongoing processes when switching documents, but don't reset memory
    if (selectedConfig?.id && previousConfigId && selectedConfig.id !== previousConfigId) {
      inputAreaRef.current?.stopProcessing();
      responseViewerRef.current?.stopStreaming();
      // Don't reset - let thread service handle memory persistence
      console.log('🔄 Home: Switched to document:', selectedConfig.name, '- memory preserved');
    }
    
    // Check if this is a chat application
    const isChatApplication = selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat';
    
    if (isChatApplication && selectedConfig) {
      // For chat applications, automatically show chat interface and collapse input panel
      setShowChatInterface(true);
      setIsInputPanelCollapsed(true);
    } else {
      // For non-chat applications, show normal interface
      setShowChatInterface(false);
      setIsInputPanelCollapsed(false);
    }
    
    // Remember the current config ID
    if (selectedConfig?.id) {
      setPreviousConfigId(selectedConfig.id);
    }
  }, [selectedConfig?.id, previousConfigId]);

  return (
    <Box className="home-container" h="100%" p={4}>
      <Flex direction="column" h="100%" className="main-content">
        <Box className="content-area" h="100%">
          {!selectedConfig ? (
            <Box className="no-config-message">
              <VStack spacing={4} align="center" justify="center" h="200px">
                <Text fontSize="2xl" fontWeight="600" color="gray.600">
                  No Configuration Selected
                </Text>
                <Text color="gray.500">
                  Please select a configuration from the sidebar or create a new one in Settings.
                </Text>
              </VStack>
            </Box>
          ) : (
            <Flex className="content-container" ref={containerRef} h="100%" direction="column" gap={2}>
              {/* Top: Input Area */}
              <Box
                className="input-area-container"
                flex={isInputPanelCollapsed ? "0 0 auto" : "0 1 auto"}
                maxH={isInputPanelCollapsed ? "auto" : "50%"}
                minH="auto"
                h={isInputPanelCollapsed ? "auto" : "auto"}
                borderRadius="md"
                bg={bgColor}
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                overflowY="auto"
                transition="all 0.3s ease"
              >
                <InputArea
                  ref={inputAreaRef}
                  config={selectedConfig}
                  onResponse={handleResponse}
                  onError={handleError}
                  onStartRequest={handleStartRequest}
                  onComplete={handleComplete}
                  onGitLabFileUploadRequest={onGitLabFileUploadRequest}
                  onGitLabSourcePathUpdate={setGitlabSourcePath}
                  onToggleGitlabBrowser={onToggleGitlabBrowser}
                  onPanelCollapse={setIsInputPanelCollapsed}
                  onInputChange={setCurrentInputs}
                />
              </Box>

              {/* Resize Handle - Only show when not collapsed and not chat application */}
              {!isInputPanelCollapsed && !(selectedConfig?.requestUrl?.includes('chat-messages') || selectedConfig?.applicationType === 'chat') && (
                <Box
                  className="resize-handle"
                  h="12px"
                  cursor="row-resize"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  zIndex={10}
                >
                  <Box
                    className="resize-handle-bar"
                    w="48px"
                    h="4px"
                    borderRadius="sm"
                    bg="gray.300"
                    transition="background-color 0.2s ease"
                    _hover={{ bg: 'blue.500', opacity: 0.7 }}
                  />
                </Box>
              )}
              
              {/* Docked Input Area Indicator */}
              {isInputPanelCollapsed && (
                <Box
                  className="docked-indicator"
                  h="2px"
                  bg="blue.500"
                  opacity={0.7}
                  transition="opacity 0.2s ease"
                />
              )}

              {/* Bottom: Response Area */}
              <Box
                className="response-area-container"
                flex="1"
                borderRadius="md"
                bg={bgColor}
                boxShadow="sm"
                border="1px"
                borderColor={borderColor}
                overflow="hidden"
                minH="200px"
                h="100%"
                transition="all 0.3s ease"
              >
                {showChatInterface && selectedConfig ? (
                  <AssistantChatClientRefactored
                    key="main-chat-client"
                    ref={assistantChatRef}
                    config={selectedConfig}
                    initialInputs={currentInputs}
                    onError={handleError}
                    onClose={() => {
                      setShowChatInterface(false);
                      setIsInputPanelCollapsed(false);
                    }}
                    onExpandInputPanel={() => {
                      inputAreaRef.current?.togglePanel();
                    }}
                    isInputPanelCollapsed={isInputPanelCollapsed}
                  />
                ) : (
                  <ResponseViewer
                    key="main-response-viewer"
                    ref={responseViewerRef}
                    config={selectedConfig}
                    onStop={handleStop}
                    gitlabSourcePath={gitlabSourcePath}
                  />
                )}
              </Box>
            </Flex>
          )}
        </Box>
      </Flex>
    </Box>
  );
};